import React, { useState, useEffect } from 'react';
import CustomSelect from '@/components/common/CustomSelect';
import Button from '@/components/common/Button';
import ValidatedInput from '@/components/common/ValidatedInput';
import { useSucursal } from '@/context/SucursalContext';
import * as repuestosApiService from '@/services/repuestosApiService';
import { FaCog, FaFileAlt, FaTags, FaDollarSign, FaBoxes, FaWarehouse, FaMapMarkerAlt, FaChartLine } from 'react-icons/fa';
import Switch from '@/components/common/Switch';
import toastService from '@/utils/toastService';
import { normalizeDataObject } from '../../utils/normalizationUtils';

/**
 * Formulario para crear o editar repuestos
 * Con layout espaciado y usando el componente SucursalSelector
 * Corregido para usar la sucursal del sidebar
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.initialData - Datos iniciales del repuesto (para edición)
 * @param {Array} props.sucursales - Lista de sucursales disponibles
 * @param {Array} props.categorias - Lista de categorías disponibles
 * @param {function} props.onSubmit - Función a ejecutar al enviar el formulario
 * @param {function} props.onCancel - Función a ejecutar al cancelar
 * @param {function} props.onStatusChange - Función a ejecutar cuando cambia el estado
 * @returns {JSX.Element} - Componente de formulario de repuesto
 */
const RepuestoForm = ({ 
  initialData = null, 
  sucursales = [], 
  categorias = [], 
  onSubmit, 
  onCancel,
  onStatusChange 
}) => {
  // Obtener la sucursal seleccionada del contexto
  const { selectedSucursal } = useSucursal();

  const [formData, setFormData] = useState({
    Nombre: '',
    Descripcion: '',
    PrecioVenta: '',
    PrecioCompra: '',
    categoria_id: '',
    Sucursal: selectedSucursal || '', // Usar sucursal del sidebar por defecto
    Stock: 0,
    StockMinimo: 0,
    Ubicacion: '',
    Disponible: true
  });
  
  const [margen, setMargen] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Obtener nombre de sucursal por ID
  const getSucursalNombre = (sucursalId) => {
    const sucursal = sucursales.find(s => s.id === sucursalId);
    return sucursal ? sucursal.Nombre : 'Desconocida';
  };

  // Si hay datos iniciales, actualizar el estado del formulario
  useEffect(() => {
    if (initialData && Object.keys(initialData).length) {
      // Normalizar datos iniciales para manejar cualquier formato
      const normalizedData = normalizeDataObject(initialData, 'repuesto');
      
      // Construir el objeto de datos del formulario
      const newFormData = {
        Nombre: normalizedData.nombre || normalizedData.Nombre || '',
        Descripcion: normalizedData.descripcion || normalizedData.Descripcion || '',
        PrecioVenta: normalizedData.precioVenta || normalizedData.PrecioVenta || '',
        PrecioCompra: normalizedData.precioCompra || normalizedData.PrecioCompra || '',
        categoria_id: normalizedData.categoriaId || normalizedData.categoria_id || '',
        // Mantener la sucursal del contexto o del repuesto si existe
        Sucursal: selectedSucursal || normalizedData.sucursal || normalizedData.Sucursal || '',
        Stock: normalizedData.stock || normalizedData.Stock || 0,
        StockMinimo: normalizedData.stockMinimo || normalizedData.StockMinimo || 0,
        Ubicacion: normalizedData.ubicacion || normalizedData.Ubicacion || '',
        Disponible: normalizedData.disponible !== false && normalizedData.Disponible !== false
      };
      
      // Si la sucursal viene como array, tomar el primer elemento
      if (Array.isArray(newFormData.Sucursal) && newFormData.Sucursal.length > 0) {
        newFormData.Sucursal = newFormData.Sucursal[0];
      }
      
      setFormData(newFormData);
      
      // Calcular margen
      if (newFormData.PrecioVenta && newFormData.PrecioCompra) {
        calculateMargin(newFormData.PrecioVenta, newFormData.PrecioCompra);
      }
    }
  }, [initialData, selectedSucursal]);

  // Si cambia la sucursal del contexto, actualizarla en el formulario
  useEffect(() => {
    if (selectedSucursal) {
      setFormData(prevData => ({
        ...prevData,
        Sucursal: selectedSucursal
      }));
    }
  }, [selectedSucursal]);

  // Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    
    // Para campos numéricos, convertir a número
    const newValue = type === 'number' ? (value ? Number(value) : '') : value;
    
    setFormData(prev => ({ 
      ...prev, 
      [name]: newValue
    }));
    
    // Actualizar margen si cambia precio de venta o compra
    if (name === 'PrecioVenta' || name === 'PrecioCompra') {
      const precioVenta = name === 'PrecioVenta' ? newValue : formData.PrecioVenta;
      const precioCompra = name === 'PrecioCompra' ? newValue : formData.PrecioCompra;
      
      calculateMargin(precioVenta, precioCompra);
    }
  };
  
  // Calcular margen de ganancia
  const calculateMargin = (precioVenta, precioCompra) => {
    if (precioVenta && precioCompra && precioCompra > 0) {
      const ganancia = precioVenta - precioCompra;
      const margenCalculado = (ganancia / precioCompra) * 100;
      setMargen(margenCalculado.toFixed(0));
    } else {
      setMargen(0);
    }
  };

  // Manejar toggle del estado disponible/no disponible
  const handleToggleDisponible = async (newDisponible) => {
    // Solo permitir toggle si estamos editando (hay initialData con id)
    if (!initialData || !initialData.id) {
      return;
    }

    // Si no se pasa parámetro, usar el valor actual invertido
    if (typeof newDisponible !== 'boolean') {
      newDisponible = !formData.Disponible;
    }
    
    try {
      // Actualizar inmediatamente en la base de datos - solo enviar los campos necesarios
      await repuestosApiService.updateRepuesto(initialData.id, {
        activo: newDisponible
      });
      
      // Actualizar el estado local
      setFormData(prev => ({ ...prev, Disponible: newDisponible }));
      
      // Mostrar mensaje de éxito
      toastService.success(`Repuesto ${newDisponible ? 'activado' : 'desactivado'} correctamente`);
      
      // Llamar callback para refrescar la lista
      if (onStatusChange) {
        onStatusChange();
      }
      
      // Cerrar modal después de cambiar el estado para forzar refresh de la lista
      setTimeout(() => {
        if (onCancel) {
          onCancel();
        }
      }, 1000); // Esperar 1 segundo para que se vea el mensaje
      
    } catch (error) {
      console.error('Error al actualizar estado del repuesto:', error);
      toastService.error('Error al actualizar el estado del repuesto');
    }
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    // Usar la sucursal del contexto si no está definida en el form
    const sucursalFinal = formData.Sucursal || selectedSucursal;
    
    // Validar que exista una sucursal
    if (!sucursalFinal) {
      alert("Por favor, selecciona una sucursal para el repuesto.");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Preparar datos para enviar
      const submitData = {
        ...formData,
        Sucursal: sucursalFinal
      };
      
      // Normalizar datos antes de enviar
      const normalizedFormData = normalizeDataObject(submitData, 'repuesto');
      
      //console.log("Enviando formulario con sucursal:", normalizedFormData.Sucursal);
      await onSubmit(normalizedFormData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="text-white">

      <div className="md:grid md:grid-cols-2 md:gap-6">
        {/* Columna izquierda: Información Básica */}
        <div className="bg-gray-800 p-4 rounded-lg mb-6 md:mb-0 border border-gray-600">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <FaCog className="text-blue-400 mr-2" />
            Información Básica
          </h3>
          
          {/* Nombre del repuesto */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium mb-2">
              <FaCog className="text-gray-400 mr-2 text-xs" />
              Nombre del repuesto<span className="text-red-500 ml-1">*</span>
            </label>
            <ValidatedInput
              type="text"
              name="Nombre"
              placeholder="Ej: Filtro de aceite premium"
              value={formData.Nombre}
              onChange={handleChange}
              required
              requiredMessage='Nombre del repuesto es requerido'
            />
          </div>
          
          {/* Descripción */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium mb-2">
              <FaFileAlt className="text-gray-400 mr-2 text-xs" />
              Descripción
            </label>
            <textarea
              name="Descripcion"
              placeholder="Describe el repuesto..."
              value={formData.Descripcion}
              onChange={handleChange}
              className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
              rows={3}
            />
          </div>
          
          {/* Categoría */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium mb-2">
              <FaTags className="text-gray-400 mr-2 text-xs" />
              Categoría<span className="text-red-500 ml-1">*</span>
            </label>
            <CustomSelect
              name="categoria_id"
              value={formData.categoria_id}
              onChange={handleChange}
              placeholder="Seleccionar categoría"
              options={categorias.map(cat => ({
                id: cat.id,
                value: cat.id,
                label: cat.nombre
              }))}
              className="w-full"
              required
              requiredMessage='Categoría es requerida'
            />
          </div>
          
        </div>
        
        {/* Columna derecha: Inventario y Precios */}
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-600">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <FaWarehouse className="text-green-400 mr-2" />
            Inventario y Precios
          </h3>
          
          {/* Sucursal oculta */}
          <input 
            type="hidden" 
            name="Sucursal" 
            value={formData.Sucursal || selectedSucursal || ''} 
          />
          
          {/* Ubicación en taller */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium mb-2">
              <FaMapMarkerAlt className="text-gray-400 mr-2 text-xs" />
              Ubicación en taller
            </label>
            <input
              type="text"
              name="Ubicacion"
              placeholder="Ej: Estante A1, Bodega B2"
              value={formData.Ubicacion}
              onChange={handleChange}
              className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
            />
          </div>
          
          {/* Stock inicial y mínimo en la misma fila */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="flex items-center text-sm font-medium mb-2">
                <FaBoxes className="text-gray-400 mr-2 text-xs" />
                Stock inicial
              </label>
              <ValidatedInput
                type="number"
                name="Stock"
                placeholder="0"
                value={formData.Stock}
                onChange={handleChange}
                className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                min="1"
                required
                requiredMessage='Stock inicial es requerido'
              />
            </div>
            <div>
              <label className="flex items-center text-sm font-medium mb-2">
                <FaWarehouse className="text-gray-400 mr-2 text-xs" />
                Stock mínimo
              </label>
              <ValidatedInput
                type="number"
                name="StockMinimo"
                placeholder="0"
                value={formData.StockMinimo}
                onChange={handleChange}
                className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                min="0"
                required
                requiredMessage='Stock mínimo es requerido'
              />
            </div>
          </div>
          
          {/* Separador visual */}
          <div className="border-t border-gray-600 pt-4 mt-4">
            {/* Título con margen de ganancia */}
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center">
                <FaDollarSign className="text-green-400 mr-2" />
                <h4 className="font-medium text-green-300">Precios</h4>
              </div>
              <div className="flex items-center space-x-2 bg-blue-900/30 px-3 py-1 rounded-full">
                <FaChartLine className="text-blue-400 text-sm" />
                <span className="text-gray-300 text-sm">Margen:</span>
                <span className="font-medium text-blue-400 text-sm">{margen}%</span>
              </div>
            </div>
            
            {/* Precios de compra y venta */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
              <div>
                <label className="flex items-center text-sm font-medium mb-2">
                  <FaDollarSign className="text-gray-400 mr-2 text-xs" />
                  Precio compra
                </label>
                <input
                  type="number"
                  name="PrecioCompra"
                  placeholder="Costo"
                  value={formData.PrecioCompra}
                  onChange={handleChange}
                  className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  min="0"
                />
              </div>
              <div>
                <label className="flex items-center text-sm font-medium mb-2">
                  <FaDollarSign className="text-gray-400 mr-2 text-xs" />
                  Precio venta<span className="text-red-500 ml-1">*</span>
                </label>
                <ValidatedInput
                  type="number"
                  name="PrecioVenta"
                  placeholder="Venta"
                  value={formData.PrecioVenta}
                  onChange={handleChange}
                  className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  required
                  min="0"
                  requiredMessage='Precio requerido'
                />
              </div>
            </div>
            
            <p className="text-xs text-gray-400 flex items-center">
              <FaChartLine className="mr-1" />
              El margen se calcula automáticamente
            </p>
          </div>
          
        </div>
      </div>
      
      {/* Separador visual y botones */}
      <div className="border-t border-gray-600 pt-6 mt-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
          {/* Switch de disponibilidad */}
          <div className="order-1 sm:order-1">
            <Switch
              checked={formData.Disponible}
              onChange={handleToggleDisponible}
              activeLabel="Repuesto disponible"
              inactiveLabel="Repuesto no disponible"
              size="md"
            />
          </div>
          
          {/* Botones */}
          <div className="flex flex-col sm:flex-row gap-3 order-2 sm:order-2 md:justify-end">
            <Button
              variant="secondary"
              onClick={onCancel}
              className="w-full sm:w-auto py-2.5 px-6 text-sm font-medium order-1"
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting}
              className="w-full sm:w-auto py-2.5 px-6 text-sm font-medium order-2"
            >
              {initialData.id ? 'Actualizar' : 'Guardar'} Repuesto
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default RepuestoForm;